import { EthereumClient, w3mConnectors, w3mProvider } from '@web3modal/ethereum'
import { Web3Modal } from "@web3modal/html"
import { configureChains, createClient, signMessage, watchAccount, getAccount, prepareWriteContract, writeContract, readContract, switchNetwork, getNetwork, fetchBalance } from "@wagmi/core"
import { polygon, polygonMumbai } from "@wagmi/core/chains"
import { publicProvider } from '@wagmi/core/providers/public'

const defaultChain = window.voidgeConfig.polygonTestnet ? polygonMumbai : polygon

// Wagmi provider
const { provider } = configureChains(
	[defaultChain],
	[
		publicProvider(),
		w3mProvider({ projectId: window.voidgeConfig.walletConnectProjectId })
	]
)

// Wagmi client
const wagmiClient = createClient({
	autoConnect: true,
	connectors: w3mConnectors({ projectId: window.voidgeConfig.walletConnectProjectId, version: 2, defaultChain }),
	provider
})

// Web3Modal and Ethereum Client
const ethereumClient = new EthereumClient(wagmiClient, [defaultChain])
const web3modal = new Web3Modal({ projectId:window.voidgeConfig.walletConnectProjectId }, ethereumClient)
web3modal.setDefaultChain(defaultChain)


export class UnityWeb3 {
	constructor(unityInstance) {
		this.unityInstance = unityInstance
		this.cachedContractAbi = {}
		this.delegateCashWallets = []

		window.voidgeFunctions = {
			gameStart: () =>{
				if (this.gameStateCallback){
					this.gameStateCallback()
				}
			},
			openConnectModal: () => {
				if (getAccount().address){
					this.connectWallet(getAccount());
				}
				else{
					web3modal.openModal({ route: "ConnectWallet" })
				}
			},
			signLoginMessage: async () => {
				try {
					const signature = await signMessage({
						message: window.voidgeConfig.loginMessage
					})
					this.unityInstance.SendMessage(window.voidgeConfig.proxyGameObject, 'LoginMessageSigned', signature)
					if (window.voidgeConfig.debug) console.log(`"${window.voidgeConfig.proxyGameObject}":LoginMessageSigned(${signature.slice(0, 5)}...)`)
				}
				catch {
					this.unityInstance.SendMessage(window.voidgeConfig.proxyGameObject, 'LoginMessageReject')
					if (window.voidgeConfig.debug) console.log(`"${window.voidgeConfig.proxyGameObject}":LoginMessageReject()`)
				}
			},
			getConnectedAddress: () => {
				return getAccount().address
			},
			writeContract: async (contractAddress, functionName, args, nonce) => {
				const writeConfig = {
					address: contractAddress,
					functionName: functionName,
					args: JSON.parse(args)
				}
	
				this.voidgeWriteContract(writeConfig, nonce)
			},
			writeContractPayable: async (payableAmount, contractAddress, functionName, args, nonce) => {
				const writeConfig = {
					address: contractAddress,
					functionName: functionName,
					args: JSON.parse(args),
					overrides: {
						value: payableAmount,
					},
				}
	
				this.voidgeWriteContract(writeConfig, nonce)
			},
			readContract: async (contractAddress, functionName, args, nonce) => {
				let abiJson = await this.getContractAbi(contractAddress)
	
				const data = {
					raw: await readContract({
						address: contractAddress,
						abi: abiJson,
						functionName: functionName,
						args: JSON.parse(args)
					})
				}
	
				const response = {
					data: JSON.stringify(data),
					nonce: nonce
				}
				const responseJson = JSON.stringify(response)
	
				this.unityInstance.SendMessage(window.voidgeConfig.proxyGameObject, 'ReadContractData', responseJson)
				if (window.voidgeConfig.debug) console.log(`"${window.voidgeConfig.proxyGameObject}":ReadContractData(${responseJson})`)
			},
			updateBalance: async () => {
				if (!getAccount().address) return
	
				const balance = await fetchBalance({
					address: getAccount().address,
					chainId: defaultChain
				})
	
				this.unityInstance.SendMessage(window.voidgeConfig.proxyGameObject, 'OnUpdateBalance', balance.value._hex)
				if (window.voidgeConfig.debug) console.log(`"${window.voidgeConfig.proxyGameObject}":OnUpdateBalance(${balance.value._hex})`)
			},
			updateTokenBalance: async (tokenAddress) => {
				if (!getAccount().address) return
	
				const balance = await fetchBalance({
					address: getAccount().address,
					chainId: defaultChain,
					token: tokenAddress
				})
	
				this.unityInstance.SendMessage(window.voidgeConfig.proxyGameObject, 'OnUpdateTokenBalance', tokenAddress + "|" + balance.value._hex)
				if (window.voidgeConfig.debug) console.log(`"${window.voidgeConfig.proxyGameObject}":OnUpdateTokenBalance(${tokenAddress + "|" + balance.value._hex})`)
	
				return balance.formatted
			}
		}

		watchAccount(account => {
			this.connectWallet(account)
		})

		web3modal.subscribeModal(newState => {
			this.sendModalState(newState.open ? 1 : 0)
		})
	
		this.unityInstance.SendMessage(window.voidgeConfig.proxyGameObject, 'VoidgeWeb3Initialized')
	}

	async connectWallet(account){
		if (account.address) {
			this.delegateCashWallets = await this.fetchDelegateCashWallets(account.address)

			if (this.delegateCashWallets.length == 1) {
				this.unityInstance.SendMessage(window.voidgeConfig.proxyGameObject, 'OnConnect', "")
				if (window.voidgeConfig.debug) console.log(`"${window.voidgeConfig.proxyGameObject}":OnConnect("")`)
			}
			else {
				if (this.delegateCashCallback){
					this.delegateCashCallback()
				}
			}
		}
		else {
			this.unityInstance.SendMessage(window.voidgeConfig.proxyGameObject, 'OnDisconnect')
			if (window.voidgeConfig.debug) console.log(`"${window.voidgeConfig.proxyGameObject}":OnDisconnect()`)
		}
	}

	async voidgeWriteContract(configData, nonce) {

		let config = {}

		try {
			// Change network
			const { chain } = getNetwork()
			if (chain.id != defaultChain.id) {
				await switchNetwork({ chainId: defaultChain.id, })
			}

			// Getting contract ABI
			configData.abi = await this.getContractAbi(configData.address)

			// Prepare write contract
			config = await prepareWriteContract(configData)
		}
		catch (e) {
			const response = {
				data: e,
				nonce: nonce
			}
			const responseJson = JSON.stringify(response)

			this.unityInstance.SendMessage(window.voidgeConfig.proxyGameObject, 'TransactionFailed', responseJson)
			if (window.voidgeConfig.debug) console.log(`"${window.voidgeConfig.proxyGameObject}":TransactionFailed(${responseJson})`)
			return
		}

		// Fix gas limit
		config.request.gasLimit = Math.ceil(config.request.gasLimit * window.voidgeConfig.gasFeeMultiplier)

		this.unityInstance.SendMessage(window.voidgeConfig.proxyGameObject, 'TransactionStart')
		if (window.voidgeConfig.debug) console.log(`"${window.voidgeConfig.proxyGameObject}":TransactionStart()`)

		let data = null
		try {
			data = await writeContract(config)
			this.unityInstance.SendMessage(window.voidgeConfig.proxyGameObject, 'TransactionPending', data.hash)
			if (window.voidgeConfig.debug) console.log(`"${window.voidgeConfig.proxyGameObject}":TransactionPending(${data.hash})`)
		}
		catch {
			const response = {
				nonce: nonce
			}
			const responseJson = JSON.stringify(response)

			this.unityInstance.SendMessage(window.voidgeConfig.proxyGameObject, 'TransactionDenied', responseJson)
			if (window.voidgeConfig.debug) console.log(`"${window.voidgeConfig.proxyGameObject}":TransactionDenied(${responseJson})`)
			return
		}

		let result = await data.wait(window.voidgeConfig.txConfirmations)
		if (result.status == 1) {
			const response = {
				data: JSON.stringify(result),
				nonce: nonce
			}
			const responseJson = JSON.stringify(response)

			this.unityInstance.SendMessage(window.voidgeConfig.proxyGameObject, 'TransactionSucceeded', responseJson)
			if (window.voidgeConfig.debug) console.log(`"${window.voidgeConfig.proxyGameObject}":TransactionSucceeded(${responseJson})`)
		}
		else {
			const response = {
				data: JSON.stringify(result),
				nonce: nonce
			}
			const responseJson = JSON.stringify(response)

			this.unityInstance.SendMessage(window.voidgeConfig.proxyGameObject, 'TransactionFailed', responseJson)
			if (window.voidgeConfig.debug) console.log(`"${window.voidgeConfig.proxyGameObject}":TransactionFailed(${responseJson})`)
		}
	}

	async getContractAbi(contractAddress) {
		contractAddress = contractAddress.toLowerCase()

		if (!this.cachedContractAbi[contractAddress]) {
			let abiJsonPath = `${window.voidgeConfig.contractsAbiRepository}/${contractAddress}.json`
			let abiJsonResponse = await fetch(abiJsonPath)
			this.cachedContractAbi[contractAddress] = await abiJsonResponse.json()

			if (window.voidgeConfig.debug) console.log(`Putting ${contractAddress} ABI in cache`)
		}
		return this.cachedContractAbi[contractAddress]
	}

	sendModalState(state) {
		this.unityInstance.SendMessage(window.voidgeConfig.proxyGameObject, 'ChangeModalState', state)
		if (window.voidgeConfig.debug) console.log(`"${window.voidgeConfig.proxyGameObject}":ChangeModalState(${state})`)
	}

	async fetchDelegateCashWallets(walletAddress) {
		let abiJson = await this.getContractAbi(window.voidgeConfig.delegateCashAddress)

		let vaultsRaw = await readContract({
			address: window.voidgeConfig.delegateCashAddress,
			abi: abiJson,
			functionName: "getDelegationsByDelegate",
			args: [walletAddress]
		})

		let vaults = []
		vaults.push({
			wallet: walletAddress,
			isVault: false
		})
		for (let e of vaultsRaw) {
			vaults.push({
				wallet: e[1],
				isVault: true
			})
		}

		return vaults
	}

	watchDelegateCash(delegateCashCallback){
		this.delegateCashCallback = delegateCashCallback
	}

	watchGameState(gameStateCallback){
		this.gameStateCallback = gameStateCallback;
	}
}